import React, { useEffect, useState } from 'react';
import { Drawer, Form, Input, Select, Button, Space, Typography, Divider, App, Alert } from 'antd';
import api from '../../services/api';
import { useLanguage } from '../../providers/LanguageProvider';

const { Title } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
  editing: any | null;
  reload: (showMessage?: boolean) => void;
  loading: boolean;
};

export default function InvoiceForm({ open, onClose, editing, reload, loading }: Props) {
  const { t } = useLanguage();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Array<{ field: string; _message: string }>>([]);

  // AntD message API (App context)
  const { message: messageApi } = App.useApp();

  useEffect(() => {
    if (editing) {
      form.setFieldsValue(editing);
    } else {
      form.resetFields();
    }
  }, [editing, form]);

  const handleSubmit = async () => {
    setValidationErrors([]);
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (editing) {
        await api.put(`/invoice-info/${editing.Id}`, values);
        messageApi.success(t('INVOICE_PROFILE_UPDATED'));
      } else {
        await api.post(`/invoice-info`, values);
        messageApi.success(t('INVOICE_PROFILE_CREATED'));
      }

      onClose();
      reload(true);
    } catch (err: any) {
      // Backend validation format
      if (err?.response?.data?.message === 'Validation error') {
        const vErrors = err?.response?.data?.errors;
        if (Array.isArray(vErrors)) {
          setValidationErrors(
            vErrors.map((e: any) => ({ field: e.field, _message: e.message }))
          );
          vErrors.forEach((e: any) => {
            // Mesajlar backend key'i ise t(key), değilse fallback olarak key döner
            messageApi.error(t(e.message));
          });
          return;
        }
      }

      // Genel backend hata anahtarı
      const errorKey = err?.response?.data?.message;
      const errorKeyDetail = err?.response?.data?.details?.error;

      if (errorKeyDetail && errorKeyDetail !== '') {
        messageApi.error(t(errorKeyDetail) !== errorKeyDetail ? t(errorKeyDetail) : t('BACKEND_INTERNAL_ERROR'));
      } else {
        messageApi.error(t(errorKey) !== errorKey ? t(errorKey) : t('BACKEND_INTERNAL_ERROR'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={editing ? t('INVOICE_EDIT_PROFILE_TITLE') : t('INVOICE_NEW_PROFILE_TITLE')}
      open={open}
      onClose={onClose}
      width={480}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose} disabled={loading}>
            {t('BTN_CANCEL')}
          </Button>
          <Button type="primary" loading={loading || saving} onClick={handleSubmit} disabled={loading}>
            {editing ? t('BTN_UPDATE') : t('BTN_SAVE')}
          </Button>
        </Space>
      }
    >
      {validationErrors.length > 0 &&
        validationErrors.map((ve, idx) => (
          <Alert
            banner
            type="error"
            message={t(ve._message)}
            key={`${ve.field || 'general'}-${idx}`}
            style={{ marginBottom: 8 }}
          />
        ))}

      <Form layout="vertical" form={form}>
        <Title level={5}>{t('INVOICE_COMPANY_INFO')}</Title>
        <Form.Item
          name="CompanyName"
          label={t('INVOICE_COMPANY_NAME')}
          rules={[{ required: true, message: t('ERROR_COMPANY_NAME_REQUIRED') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="CompanyCountry"
          label={t('LABEL_COUNTRY')}
          rules={[{ required: true, message: t('ERROR_COUNTRY_REQUIRED') }]}
        >
          <Select
            showSearch
            options={[
              { value: 'TR', label: t('COUNTRY_TR') },
              { value: 'NL', label: t('COUNTRY_NL') },
              { value: 'DE', label: t('COUNTRY_DE') },
              { value: 'FR', label: t('COUNTRY_FR') },
              { value: 'BE', label: t('COUNTRY_BE') },
              { value: 'PL', label: t('COUNTRY_PL') }
            ]}
          />
        </Form.Item>

        <Divider />

        <Title level={5}>{t('INVOICE_ADDRESS_INFO')}</Title>
        <Form.Item
          name="CompanyAddress"
          label={t('INVOICE_ADDRESS')}
          rules={[{ required: true, message: t('ERROR_ADDRESS_REQUIRED') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="CompanyCity"
          label={t('INVOICE_CITY')}
          rules={[{ required: true, message: t('ERROR_CITY_REQUIRED') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="CompanyPostalCode"
          label={t('INVOICE_POSTAL_CODE')}
          rules={[{ required: true, message: t('ERROR_POSTAL_CODE_REQUIRED') }]}
        >
          <Input />
        </Form.Item>

        <Divider />

        <Title level={5}>{t('INVOICE_TAX_INFO')}</Title>
        <Form.Item name="TaxOffice" label={t('INVOICE_TAX_OFFICE')}>
          <Input />
        </Form.Item>
        <Form.Item name="TaxNumber" label={t('INVOICE_TAX_NUMBER')}>
          <Input />
        </Form.Item>
        <Form.Item name="ChamberOfCommerceNumber" label={t('INVOICE_CHAMBER_NUMBER')}>
          <Input />
        </Form.Item>

        <Divider />

        <Title level={5}>{t('INVOICE_BANK_CONTACT')}</Title>
        <Form.Item name="IBAN" label={t('INVOICE_IBAN')}>
          <Input />
        </Form.Item>
        <Form.Item
          name="Email"
          label={t('LABEL_EMAIL')}
          rules={[
            { required: true, message: t('ERROR_EMAIL_REQUIRED') },
            { type: 'email', message: t('ERROR_EMAIL_INVALID') }
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
